import { EventEmitter } from '../../../../../FrontendBundle/Resources/js/ofcs';
import FetchError from './FetchError';

/**
 * The production data store contains the data for all loaded production data items.
 * It is used by Ofcs.Editor.Item.ItemFactoy to resolve data to create item
 * instances. Data of currently unknown items will be loaded via ajax from
 * a preconfigured url.
 */
export default class ProductionDataStore extends EventEmitter {
    /**
     * @type {ProductFamilyStore}
     * @private
     */
    productFamilies = null;

    /**
     * @type {ConnectorInterface}
     * @private
     */
    connector = null;

    /**
     * @type {Object}
     * @private
     */
    productionData = {};

    /**
     * Object with pending callbacks, categorized by its item id. If a
     * item is requested, the callback will be added to this object. So, we
     * can handle multiple requests on the same item with only one ajax call.
     *
     * @type {Object}
     * @private
     */
    pendingRequests = {};

    /**
     * This event is triggered before production data is loaded.
     *
     * @event beforeLoadData
     * @param {ProductionDataStore} me
     * @param {AbstractItem} item object to load the production data into
     * @param {number} id item id
     */

    /**
     * This event is triggered after production data was loaded.
     *
     * @event loadData
     * @param {ProductionDataStore} me
     * @param {AbstractItem} item object to load the production data into
     * @param {number} id item id
     * @param {jqXHR} jqXhr item id
     * @param {string} textStatus id
     */

    /**
     * Inititalizes the production data store
     *
     * @param {ProductFamilyStore} productFamilies Object with product families
     * @param {ConnectorInterface} connector
     * @constructor
     */
    constructor(productFamilies, connector) {
        super();
        this.productFamilies = productFamilies;
        this.connector = connector;
    }

    /**
     * Add production data to the given object
     *
     * @param {AbstractItem} item object to load the production data into
     * @param {Object[]} productionDataItems Production Data Item
     */
    addProductionData(item, productionDataItems) {
        productionDataItems
            .filter(productionDataItem => this.connector.supports(item, productionDataItem))
            .forEach((productionDataItem) => {
                const identifier = this.connector.createCacheIdentifier(item, productionDataItem.itemId);

                if (productionDataItem.itemId && !this.productionData[identifier]) {
                    this.productionData[identifier] = productionDataItem;
                }
            });
    }

    /**
     * Add product families
     *
     * @param {Object} productFamiliesData Product family data
     */
    addProductFamilies(productFamiliesData) {
        if (!productFamiliesData) return;

        Object
            .values(productFamiliesData)
            .map(catalogData => Object.values(catalogData))
            .reduce((flattened, catalogData) => flattened.concat(catalogData), [])
            .forEach(productFamilyData => this.productFamilies.addProductFamily(productFamilyData));
    }

    /**
     * Return product family
     *
     * @param {ItemInterface} item
     *
     * @return {ProductFamily}
     */
    getProductFamilyForItem(item) {
        return this.productFamilies.getByItemId(item.get('itemId'));
    }

    /**
     * Is production data available for the given production data ID?
     *
     * @param {AbstractItem} item object to load the production data into
     * @param {number} id Production data ID
     * @return {boolean}
     */
    hasProductionData(item, id) {
        const identifier = this.connector.createCacheIdentifier(item, id);

        return !!this.productionData[identifier];
    }

    /**
     * Returns production data for the given production data ID. This method
     * will return an array with exacly one item. In case the given ID
     * matches to a set the returned array contains more than one data object.
     *
     * @param {Ofcs.Editor.Item.AbstractItem} item object to load the production data into
     * @param {number} id Production data ID
     * @return {Object}
     */
    getProductionData(item, id) {
        const identifier = this.connector.createCacheIdentifier(item, id);

        return this.productionData[identifier];
    }

    /**
     * Get production data for an item.
     *
     * @param {ContainerItem} parentItem object to load the production data into
     * @param {number} id Id if the production data to load
     * @param {Function} callback
     * @param {Object} scope
     * @return {Promise.<Object>}
     */
    async callbackProductionData(parentItem, id, callback, scope) {
        if (!this.hasProductionData(parentItem, id)) {
            await this.loadData(parentItem, id);
        }

        const data = this.getProductionData(parentItem, id);
        if (callback) callback.call(scope, data);

        return data;
    }

    /**
     * Load production data for given id from external source. The URL of
     * the external source can be defined by #setUrlTemplate. The method
     * fires a synchronous ajax request.
     *
     * @private
     * @param {Ofcs.Editor.Item.AbstractItem} item object to load the production data into
     * @param {number} id Id if the production data to load
     * @param {Function} [callback]
     * @param {Object} [scope]
     * @return {Promise.<Object>}
     */
    async loadData(item, id, callback = () => {}, scope) {
        const identifier = this.connector.createCacheIdentifier(item, id);
        const boundCallback = scope ? callback.bind(scope) : callback;

        if (!this.pendingRequests[identifier]) {
            this.pendingRequests[identifier] = this.fetchData(item, id);
        }

        try {
            const items = await this.pendingRequests[identifier];
            if (boundCallback) boundCallback(items);
            return items;
        } finally {
            delete this.pendingRequests[identifier];
        }
    }

    async fetchData(item, id) {
        const url = this.connector.createUrl(item, id);

        this.trigger('beforeLoadData', this, item, id);

        try {
            const response = await fetch(url, { credentials: 'same-origin' });

            if (!response.ok) {
                throw new FetchError(response.statusText, response);
            }

            const data = await response.json();

            this.addProductionData(item, data.items);
            this.addProductFamilies(data.productFamilies);
            return data.items;
        } finally {
            this.trigger('loadData', this, item, id);
        }
    }
}
